import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  BookOpenIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import { RailbirdGame, useRailbirdGamesApi } from '../../services/railbirdGamesApi'
import { isAdmin } from '../../utils/roles'
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal'
import toast from 'react-hot-toast'

export default function RailbirdGamesPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const railbirdGamesApi = useRailbirdGamesApi()
  const [searchTerm, setSearchTerm] = useState('')
  const [deletingGame, setDeletingGame] = useState<RailbirdGame | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    data: gamesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['railbird-games', searchTerm],
    queryFn: () => railbirdGamesApi.getGames(getAccessTokenSilently, {
      search: searchTerm || undefined,
      limit: 50
    }),
    enabled: !!user
  })

  const games = gamesData?.games || []
  const userIsAdmin = isAdmin(user)



  const handleDeleteGame = (game: RailbirdGame) => {
    setDeletingGame(game)
  }

  const confirmDeleteGame = async () => {
    if (!deletingGame) return

    try {
      setIsDeleting(true)
      await railbirdGamesApi.deleteGame(deletingGame.id)
      toast.success('Game deleted successfully')
      setDeletingGame(null)
      refetch()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete game')
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Error Loading Games</h2>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center">
            <motion.h1 
              className="text-5xl font-bold text-gray-900 dark:text-white mb-4"
              style={{ fontFamily: 'Georgia, serif' }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Railbird Games
            </motion.h1>
            <motion.p 
              className="text-xl text-amber-800 dark:text-amber-200 max-w-3xl mx-auto italic"
              style={{ fontFamily: 'Georgia, serif' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              "A collection of time-honored gambling games, poker variants, and dice diversions 
              that have graced the rails for generations — each with its own tales and traditions."
            </motion.p>
          </div>

          {/* Search and Create */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-600 w-5 h-5" />
              <input
                type="text"
                placeholder="Search games..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-amber-200 dark:border-amber-700 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur text-gray-900 dark:text-white placeholder-amber-600 dark:placeholder-amber-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                style={{ fontFamily: 'Georgia, serif' }}
              />
            </div>

            {userIsAdmin && (
              <motion.button
                onClick={() => navigate('/legacy/railbird-games/create')}
                className="flex items-center space-x-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
                style={{ fontFamily: 'Georgia, serif' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PlusIcon className="w-5 h-5" />
                <span>Add New Game</span>
              </motion.button>
            )}
          </div>

          {/* Games Grid */}
          {games.length === 0 ? (
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <BookOpenIcon className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                No games found
              </h3>
              <p className="text-amber-700 dark:text-amber-300" style={{ fontFamily: 'Georgia, serif' }}>
                {searchTerm ? 'Try adjusting your search terms.' : 'The game collection awaits your first entry.'}
              </p>
            </motion.div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {games.map((game, index) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-amber-200 dark:border-amber-700 overflow-hidden group"
                >
                  {/* Card Header with optional photo */}
                  {game.images && game.images.length > 0 ? (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={game.images[0]}
                        alt={game.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-bold truncate" style={{ fontFamily: 'Georgia, serif' }}>
                            {game.name}
                          </h3>
                          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => navigate(`/legacy/railbird-games/${game.id}`)}
                              className="p-1 hover:bg-white/20 rounded transition-colors"
                              title="View details"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            {userIsAdmin && (
                              <>
                                <button
                                  onClick={() => navigate(`/legacy/railbird-games/${game.id}/edit`)}
                                  className="p-1 hover:bg-white/20 rounded transition-colors"
                                  title="Edit game"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteGame(game)}
                                  className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                  title="Delete game"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-amber-600 to-orange-600 p-4 text-white">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold truncate" style={{ fontFamily: 'Georgia, serif' }}>
                          {game.name}
                        </h3>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => navigate(`/legacy/railbird-games/${game.id}`)}
                            className="p-1 hover:bg-white/20 rounded transition-colors"
                            title="View details"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          {userIsAdmin && (
                            <>
                              <button
                                onClick={() => navigate(`/legacy/railbird-games/${game.id}/edit`)}
                                className="p-1 hover:bg-white/20 rounded transition-colors"
                                title="Edit game"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteGame(game)}
                                className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                title="Delete game"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Card Body */}
                  <div className="p-6">
                    <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3" style={{ fontFamily: 'Georgia, serif' }}>
                      {game.description}
                    </p>

                    <div className="flex items-center justify-between text-sm text-amber-700 dark:text-amber-300 mb-4">
                      <div className="flex items-center space-x-1">
                        <UsersIcon className="w-4 h-4" />
                        <span style={{ fontFamily: 'Georgia, serif' }}>
                          Max {game.maxPlayers} players
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <BookOpenIcon className="w-4 h-4" />
                        <span style={{ fontFamily: 'Georgia, serif' }}>
                          {game.talesFromTheRail.length} tale{game.talesFromTheRail.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/legacy/railbird-games/${game.id}`)}
                      className="w-full py-2 border-2 border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-600 hover:text-white rounded-lg transition-all duration-200 font-semibold"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      View Game & Tales
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.div>



        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={!!deletingGame}
          onClose={() => setDeletingGame(null)}
          onConfirm={confirmDeleteGame}
          title="Delete Game"
          message={`Are you sure you want to delete "${deletingGame?.name}"? This will also delete all associated tales from the rail. This action cannot be undone.`}
          confirmText="Delete Game"
          isDeleting={isDeleting}
          countdownSeconds={5}
        />
      </div>
    </div>
  )
}
